<template>
  <Teleport to="body">
    <Transition name="tooltip">
      <div
        v-if="visible"
        ref="tooltipEl"
        class="tooltip-container"
        :style="tooltipStyle"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <!-- Simple text content -->
        <span v-if="typeof content === 'string'" class="tooltip-text">
          {{ content }}
        </span>

        <!-- Complex content slot -->
        <div v-else class="tooltip-content">
          <slot name="content" :data="content">
            <div v-if="typeof content === 'string'" v-html="content"></div>
          </slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  nextTick,
  withDefaults,
  defineProps,
  defineEmits,
} from "vue";

interface Props {
  visible: boolean;
  content: string | object | null;
  x: number;
  y: number;
  offset?: { x: number; y: number };
  preventHover?: boolean; // For fretboard tooltips that shouldn't interfere with mouse events
  isAbove?: boolean; // For smart positioning above/below notes
  isLeftSide?: boolean; // For left/right positioning (true = left side, false = right side)
}

const props = withDefaults(defineProps<Props>(), {
  offset: () => ({ x: 8, y: 8 }),
  preventHover: false,
  isAbove: false,
  isLeftSide: undefined,
});

const emit = defineEmits<{
  mouseenter: [event: MouseEvent];
  mouseleave: [event: MouseEvent];
}>();

const tooltipEl = ref<HTMLElement | null>(null);

const tooltipStyle = computed(() => {
  const style: Record<string, string> = {
    position: "fixed",
    left: `${props.x + props.offset.x}px`,
    top: `${props.y + props.offset.y}px`,
    zIndex: "10000",
  };

  // Handle different positioning modes
  if (props.isLeftSide !== undefined) {
    // Left/right positioning for fretboard tooltips
    if (props.isLeftSide) {
      // Position on left side - tooltip right edge aligns with anchor point
      style.transform = "translateX(-100%) translateY(-50%)";
    } else {
      // Position on right side - tooltip left edge starts at anchor point
      style.transform = "translateY(-50%)";
    }
  } else {
    // Default centered positioning for above/below tooltips
    style.transform = "translateX(-50%)";
  }

  if (props.preventHover) {
    style.pointerEvents = "none";
  }

  return style;
});

// Handle mouse events for non-fretboard tooltips
function handleMouseEnter(event: MouseEvent) {
  if (!props.preventHover) {
    emit("mouseenter", event);
  }
}

function handleMouseLeave(event: MouseEvent) {
  if (!props.preventHover) {
    emit("mouseleave", event);
  }
}

// Auto-adjust position to stay within viewport and handle overflow
watch(
  [() => props.visible, () => props.x, () => props.y, () => props.isAbove, () => props.isLeftSide],
  async () => {
    if (props.visible) {
      await nextTick();

      if (tooltipEl.value) {
        const rect = tooltipEl.value.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Handle isAbove positioning by adjusting based on actual tooltip height
        if (props.isAbove) {
          // Position tooltip above the anchor point, accounting for its full height
          const adjustedY = props.y - rect.height + props.offset.y;
          tooltipEl.value.style.top = `${adjustedY}px`;
        }

        // Handle horizontal overflow for left/right positioning
        if (props.isLeftSide !== undefined) {
          // For fretboard tooltips with left/right positioning
          if (rect.right > viewportWidth && !props.isLeftSide) {
            // Right-side tooltip overflowing: switch to left-side positioning
            // Need to adjust the x position since original was calculated for right-side
            const noteRadius = 24;
            const horizontalOffset = 8;
            const leftSideX = props.x - (noteRadius + horizontalOffset) * 2 + props.offset.x;
            tooltipEl.value.style.left = `${leftSideX}px`;
            tooltipEl.value.style.transform = "translateX(-100%) translateY(-50%)";
          } else if (rect.left < 0 && props.isLeftSide) {
            // Left-side tooltip overflowing: switch to right-side positioning
            // Need to adjust the x position since original was calculated for left-side
            const noteRadius = 24;
            const horizontalOffset = 8;
            const rightSideX = props.x + (noteRadius + horizontalOffset) * 2 + props.offset.x;
            tooltipEl.value.style.left = `${rightSideX}px`;
            tooltipEl.value.style.transform = "translateY(-50%)";
          }
        } else {
          // Handle horizontal overflow for above/below positioning (legacy)
          if (rect.right > viewportWidth) {
            tooltipEl.value.style.left = `${
              props.x - rect.width - props.offset.x
            }px`;
          }
        }

        // Adjust vertical position if tooltip would go off-screen
        if (rect.bottom > viewportHeight && !props.isAbove) {
          tooltipEl.value.style.top = `${
            props.y - rect.height - props.offset.y
          }px`;
        }

        // For above positioning, adjust if it goes above viewport
        if (props.isAbove && rect.top < 0) {
          tooltipEl.value.style.top = `${props.y + props.offset.y}px`;
        }
      }
    }
  }
);
</script>

<style scoped lang="scss">
.tooltip-container {
  background: var(--shade-10);
  border: 1px solid var(--shade-20);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  max-width: 300px;
  word-wrap: break-word;

  // Smooth position and size transitions for note-anchored tooltips
  transition: left 0.25s ease-out, top 0.25s ease-out, width 0.2s ease-out, height 0.2s ease-out;
}

.tooltip-text {
  color: var(--shade-70);
  line-height: 1.4;
}

.tooltip-content {
  color: var(--shade-70);
  line-height: 1.4;
}

/* Animations */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: all 0.15s ease-out;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: scale(0.1) translateY(4px);
}

.tooltip-enter-to,
.tooltip-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}
</style>
